import type { PropsWithChildren } from 'react';
import { useRef } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  type GestureResponderEvent,
  type PressableProps,
} from 'react-native';

type PressableScaleProps = PressableProps & {
  scaleTo?: number;
};

export function PressableScale({
  children,
  disabled,
  onPressIn,
  onPressOut,
  scaleTo = 0.985,
  ...props
}: PropsWithChildren<PressableScaleProps>) {
  const scaleRef = useRef(new Animated.Value(1));
  const scale = scaleRef.current;

  function animateScale(toValue: number) {
    Animated.timing(scale, {
      duration: toValue < 1 ? 90 : 140,
      easing: Easing.out(Easing.cubic),
      toValue,
      useNativeDriver: true,
    }).start();
  }

  function handlePressIn(event: GestureResponderEvent) {
    if (!disabled) {
      animateScale(scaleTo);
    }

    onPressIn?.(event);
  }

  function handlePressOut(event: GestureResponderEvent) {
    if (!disabled) {
      animateScale(1);
    }

    onPressOut?.(event);
  }

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Pressable
        {...props}
        disabled={disabled}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
      >
        {children}
      </Pressable>
    </Animated.View>
  );
}
